/**
 * Fordonskort — flik «Enhet / P&P» (endast is_global_admin).
 * Visar FW, konfig, ICE/PHEV/EV-profil och EYE-status.
 */

import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '@/services/api/client';
import { useTranslation } from '@/store/languageStore';

type ObdProfile = 'ICE' | 'HYBRID' | 'EV';

interface PnPStatus {
  runtime: {
    enabled: boolean;
    allowlist: string[];
    allowedModels: string[];
    observerEmail: string;
  };
  imei: string | null;
  deviceModel: string | null;
  plugAndPlay: boolean;
  currentFirmware: string | null;
  fuelType: string | null;
  suggestedProfile: string;
  profile: string;
  profileSource: string | null;
  baseline: {
    desired_firmware_file: string | null;
    desired_config_file: string | null;
  } | null;
  baselineReady: boolean;
  state: {
    status: string;
    last_step_key: string | null;
    last_step_detail: string | null;
    last_error: string | null;
    eye_reset_done: boolean;
  } | null;
  eyeActiveLinks: number;
  eyeResetDone: boolean;
  eligible: { allowed: boolean; reason?: string };
  onProblemList?: boolean;
  problemRecipe?: 'trafic' | 'obd_disable' | null;
  problemCommandPending?: boolean;
  problemCommandAppliedAt?: string | null;
  appliedParameters?: Array<{
    id: string;
    value: string;
    label: string;
    group: 'ice' | 'ev' | 'problem_list' | 'vin';
    status: 'applied' | 'planned' | 'skipped';
  }>;
}

interface VehicleDevicePnPTabProps {
  vehicleId: number;
}

function profileLabel(profile: string, t: (k: string) => string): string {
  switch (profile) {
    case 'EV':
      return t('vehicleCard.pnp.profileEv');
    case 'HYBRID':
      return t('vehicleCard.pnp.profilePheV');
    case 'ICE':
      return t('vehicleCard.pnp.profileIce');
    default:
      return profile || '—';
  }
}

function paramGroupLabel(
  group: 'ice' | 'ev' | 'problem_list' | 'vin',
  t: (k: string) => string,
): string {
  switch (group) {
    case 'ice':
      return t('vehicleCard.pnp.groupIce');
    case 'ev':
      return t('vehicleCard.pnp.groupEv');
    case 'problem_list':
      return t('vehicleCard.pnp.groupProblem');
    case 'vin':
      return t('vehicleCard.pnp.groupVin');
    default: {
      const _never: never = group;
      return _never;
    }
  }
}

const VehicleDevicePnPTab: React.FC<VehicleDevicePnPTabProps> = ({ vehicleId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<PnPStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const res = await api.get<{ success: boolean; data: PnPStatus }>(
        `/api/vehicles/${vehicleId}/obd-provisioning`,
      );
      setData(res.data?.data ?? null);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(e?.response?.data?.error || e?.message || t('vehicleCard.pnp.loadError'));
      setData(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const setProfile = async (profile: ObdProfile) => {
    setBusy(true);
    try {
      const res = await api.post<{ success: boolean; data: PnPStatus }>(
        `/api/vehicles/${vehicleId}/obd-provisioning/profile`,
        { profile },
      );
      setData(res.data?.data ?? null);
      toast.success(t('vehicleCard.pnp.profileSaved'));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(e?.response?.data?.error || e?.message || t('vehicleCard.pnp.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const togglePnP = async (enabled: boolean) => {
    if (!data?.imei) return;
    setBusy(true);
    try {
      await api.put(`/api/device-management/devices/${encodeURIComponent(data.imei)}/plug-and-play`, {
        enabled,
      });
      toast.success(enabled ? t('vehicleCard.pnp.pnpEnabled') : t('vehicleCard.pnp.pnpDisabled'));
      await load();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(e?.response?.data?.error || e?.message || t('vehicleCard.pnp.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const runSimulate = async () => {
    setBusy(true);
    try {
      await api.post(`/api/vehicles/${vehicleId}/obd-provisioning/run`, { simulate: true });
      toast.success(t('vehicleCard.pnp.simulateDone'));
      await load();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(e?.response?.data?.error || e?.message || t('vehicleCard.pnp.runError'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-sm text-textSecondary">{t('common.loading')}</div>;
  }

  if (loadFailed) {
    return (
      <div className="p-4 text-sm text-textSecondary border border-border rounded-lg bg-bgCard space-y-2">
        <p>{t('vehicleCard.pnp.loadError')}</p>
        <button
          type="button"
          className="text-sm text-blue-600 hover:underline"
          onClick={() => void load()}
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  if (!data?.imei) {
    return (
      <div className="p-4 text-sm text-textSecondary border border-border rounded-lg bg-bgCard">
        {t('vehicleCard.pnp.noDevice')}
      </div>
    );
  }

  const eyeHasParams = data.eyeActiveLinks > 0 && !data.eyeResetDone;

  return (
    <div className="space-y-4">
      {!data.runtime.enabled && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
          {t('vehicleCard.pnp.globalOff')}
        </div>
      )}

      {!data.baselineReady && (
        <div className="rounded-lg border border-orange-300 bg-orange-50 dark:bg-orange-900/20 px-4 py-3 text-sm">
          {t('vehicleCard.pnp.baselineMissing')}
        </div>
      )}

      <div className="bg-bgCard border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-textDefault mb-3 border-l-4 border-blue-500 pl-2">
          {t('vehicleCard.pnp.sectionDevice')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <Field label={t('vehicleCard.pnp.imei')} value={data.imei} mono />
          <Field label={t('vehicleCard.pnp.model')} value={data.deviceModel || '—'} />
          <Field
            label={t('vehicleCard.pnp.currentFw')}
            value={data.currentFirmware || '—'}
            mono
          />
          <Field
            label={t('vehicleCard.pnp.targetFw')}
            value={data.baseline?.desired_firmware_file || '—'}
            mono
          />
          <Field
            label={t('vehicleCard.pnp.targetConfig')}
            value={data.baseline?.desired_config_file || '—'}
            mono
          />
          <Field
            label={t('vehicleCard.pnp.status')}
            value={data.state?.status || (data.runtime.enabled ? '—' : 'disabled')}
          />
        </div>
      </div>

      <div className="bg-bgCard border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-textDefault mb-3 border-l-4 border-blue-500 pl-2">
          {t('vehicleCard.pnp.sectionProfile')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mb-3">
          <Field label={t('vehicleCard.pnp.fuelType')} value={data.fuelType || '—'} />
          <Field
            label={t('vehicleCard.pnp.profile')}
            value={profileLabel(data.profile, t)}
          />
          <Field
            label={t('vehicleCard.pnp.profileSource')}
            value={data.profileSource || '—'}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {(['ICE', 'HYBRID', 'EV'] as const).map((p) => (
            <button
              key={p}
              type="button"
              disabled={busy}
              onClick={() => void setProfile(p)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                data.profile === p
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-bgCard text-textDefault border-border hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {profileLabel(p, t)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-bgCard border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-textDefault mb-3 border-l-4 border-blue-500 pl-2">
          {t('vehicleCard.pnp.sectionParams')}
        </h3>
        <p className="text-xs text-textSecondary mb-2">{t('vehicleCard.pnp.paramsHint')}</p>
        <p className="text-xs text-textSecondary mb-1">
          {data.onProblemList
            ? data.problemRecipe === 'obd_disable'
              ? t('vehicleCard.pnp.onProblemListDisable')
              : t('vehicleCard.pnp.onProblemList')
            : t('vehicleCard.pnp.notOnProblemList')}
        </p>
        {data.onProblemList ? (
          <p className="text-xs mb-3">
            {data.problemCommandPending || !data.problemCommandAppliedAt
              ? t('vehicleCard.pnp.problemListPending')
              : t('vehicleCard.pnp.problemListApplied', {
                  when: new Date(data.problemCommandAppliedAt).toLocaleString('sv-SE'),
                })}
          </p>
        ) : null}
        {(data.appliedParameters ?? []).length === 0 ? (
          <div className="text-sm text-textSecondary">—</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-textSecondary border-b border-border">
                  <th className="py-1 pr-3 font-medium">{t('vehicleCard.pnp.paramId')}</th>
                  <th className="py-1 pr-3 font-medium">{t('vehicleCard.pnp.paramValue')}</th>
                  <th className="py-1 pr-3 font-medium">{t('vehicleCard.pnp.paramLabel')}</th>
                  <th className="py-1 pr-3 font-medium">{t('vehicleCard.pnp.paramGroup')}</th>
                  <th className="py-1 font-medium">{t('vehicleCard.pnp.paramStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.appliedParameters ?? []).map((row) => (
                  <tr key={`${row.group}-${row.id}`} className="border-b border-border/60">
                    <td className="py-1.5 pr-3 font-mono text-xs">{row.id}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs break-all">{row.value}</td>
                    <td className="py-1.5 pr-3">{row.label}</td>
                    <td className="py-1.5 pr-3">{paramGroupLabel(row.group, t)}</td>
                    <td className="py-1.5">
                      <span
                        className={
                          row.status === 'applied'
                            ? 'text-green-700 dark:text-green-400'
                            : 'text-textSecondary'
                        }
                      >
                        {row.status === 'applied'
                          ? t('vehicleCard.pnp.statusApplied')
                          : row.status === 'skipped'
                            ? t('vehicleCard.pnp.statusSkipped')
                            : t('vehicleCard.pnp.statusPlanned')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-bgCard border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-textDefault mb-3 border-l-4 border-blue-500 pl-2">
          {t('vehicleCard.pnp.sectionEye')}
        </h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field
            label={t('vehicleCard.pnp.eyeParams')}
            value={
              eyeHasParams
                ? t('vehicleCard.pnp.eyeYes')
                : data.eyeResetDone
                  ? t('vehicleCard.pnp.eyeReset')
                  : t('vehicleCard.pnp.eyeNo')
            }
          />
          <Field
            label={t('vehicleCard.pnp.eyeLinks')}
            value={String(data.eyeActiveLinks)}
          />
        </div>
      </div>

      {data.state?.last_step_detail && (
        <div className="text-xs text-textSecondary px-1">
          {data.state.last_step_key}: {data.state.last_step_detail}
          {data.state.last_error ? ` (${data.state.last_error})` : ''}
        </div>
      )}

      {!data.eligible.allowed && data.runtime.enabled && (
        <div className="text-sm text-amber-700 dark:text-amber-300">
          {data.eligible.reason}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => void togglePnP(!data.plugAndPlay)}
          className="px-3 py-2 rounded-lg text-sm bg-gray-100 dark:bg-gray-800 text-textDefault hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          {data.plugAndPlay ? t('vehicleCard.pnp.disablePnp') : t('vehicleCard.pnp.enablePnp')}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void runSimulate()}
          className="px-3 py-2 rounded-lg text-sm bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200"
        >
          {t('vehicleCard.pnp.simulate')}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void load()}
          className="px-3 py-2 rounded-lg text-sm border border-border"
        >
          {t('common.refresh')}
        </button>
      </div>
    </div>
  );
};

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-textSecondary">{label}</div>
      <div className={`font-medium text-textDefault break-all ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </div>
    </div>
  );
}

export default React.memo(VehicleDevicePnPTab);
